import React, { useState } from 'react';
import Request from 'files/Request.js';

import CallModal from 'views/CallModal.js';
import Utils from 'files/Utils.js';
import { EVENTS, logBookingEvent } from 'files/Analytics.js';

const FIRE = '#D63D1F';
const FIRE_GLOW = '#FF6B3D';
const PRIMARY_GREEN = '#1B5E20';
const ACCENT_GREEN = '#2E8B57';
const SECONDARY_DARK = '#242F3F';

const Hero = ({ utils }) => {

    const isMobile = Utils.isMobile();
    const isCanada = Utils.isCanadianVisitor();
    const phoneNumber = Utils.getPhoneNumber();

    const [zip, setZip] = useState('');
    const [zipError, setZipError] = useState('');
    const [showSchedule, setShowSchedule] = useState(false);
    const [callModalVisible, setCallModalVisible] = useState(false);

    const onCallNow = () => {

        // Primary CTA: attempt the dialer, falling back to the call-info modal
        // when no tel: handler is available (see Utils.attemptPhoneCall).
        logBookingEvent(EVENTS.PHONE_NUMBER_CLICK, { phone_number: phoneNumber, cta: 'call_now', form_location: 'hero' });
        utils.booking.showCallnow();
    }

    const onCallMe = () => {
        // Secondary CTA: capture the lead first, then a local authorized dealer
        // calls them back. Opens the "Call Me" callback flow (BookingLayer).
        logBookingEvent(EVENTS.PHONE_NUMBER_CLICK, { phone_number: phoneNumber, cta: 'call_me', form_location: 'hero' });
        utils.booking.showCallback();
    }

    const onScheduleToggle = () => {
        setShowSchedule(prev => !prev);
    }

    const onZipChange = evt => {
        let next = (evt.target.value || '').replace(/\D/g, '').slice(0, 5);
        setZip(next);
        if(zipError) {
            setZipError('');
        }
    }

    const onZipSubmit = async evt => {
        try {

            // prepare value for request
            evt.preventDefault();
            let trimmed = zip.trim();

            // reset zip error field
            setZipError('');

            // track booking-funnel step 1: zip code entered + inspection requested
            logBookingEvent(EVENTS.SCHEDULE_INSPECTION, { form_location: 'hero', funnel_step: 1 });

            // submit request to server for
            let { dealerships } = await Request.get(utils, '/dealerships/', {
                type: 'optimal_dealership_for_zipcode',
                zipcode: trimmed
            });

            // throw an error if no dealership was found for the provided zip code
            if(dealerships.length === 0) {
                utils.alert.show({
                    title: 'No Dealers Found',
                    message: 'It looks like there are no authorized dealers in your area at the moment.'
                }); 
                return;
            }

            // launch booking modal with recommended dealership
            utils.booking.show(trimmed);
            
        } catch(e) {
            setZipError(e.message);
        }
    }

    const onClaimClick = () => {
        // track booking-funnel step 2: "Claim My Free Kit" clicked
        logBookingEvent(EVENTS.CLAIM_KIT, { funnel_step: 2 });
        // launch the "Call Me" callback flow so we capture the lead and have a
        // local authorized dealer reach out about the free kit
        utils.booking.showCallback();
    }

    const getZipForm = () => {
        return (
            <form
            onSubmit={onZipSubmit}
            style={{
                marginTop: 16,
                maxWidth: 440,
                width: '100%'
            }}>
                <label
                htmlFor={'hero-zip'}
                style={{
                    color: 'rgba(255,255,255,0.9)',
                    display: 'block',
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: 13,
                    fontWeight: 600,
                    letterSpacing: 1.5,
                    marginBottom: 8,
                    textTransform: 'uppercase'
                }}>{`Enter your ${isCanada ? 'postal code' : 'ZIP code'} to find your local inspector`}</label>
                <div style={{
                    alignItems: isMobile ? 'stretch' : 'center',
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: 12
                }}>
                    <div style={{
                        flex: isMobile ? 'none' : 1,
                        position: 'relative'
                    }}>
                        <i
                        className={'fas fa-map-marker-alt'}
                        style={{
                            color: FIRE,
                            fontSize: 18,
                            left: 16,
                            pointerEvents: 'none',
                            position: 'absolute',
                            top: '50%',
                            transform: 'translateY(-50%)'
                        }} />
                        <input
                        autoComplete={'postal-code'}
                        id={'hero-zip'}
                        inputMode={'numeric'}
                        maxLength={5}
                        onChange={onZipChange}
                        placeholder={`Enter Your ${isCanada ? 'Postal Code' : 'ZIP code'}`}
                        type={'text'}
                        value={zip}
                        style={{
                            backgroundColor: '#FFFFFF',
                            border: `1px solid ${zipError ? FIRE : 'rgba(220,220,220,0.4)'}`,
                            borderRadius: 8,
                            color: '#1E1E1E',
                            fontFamily: '"Open Sans", sans-serif',
                            fontSize: 16,
                            height: 48,
                            outline: 'none',
                            padding: '0 20px 0 44px'
                        }} />
                    </div>
                    <button
                    type={'submit'}
                    style={{
                        alignItems: 'center',
                        background: `linear-gradient(90deg, ${FIRE}, ${FIRE_GLOW})`,
                        border: 'none',
                        borderRadius: 8,
                        color: '#FFFFFF',
                        cursor: 'pointer',
                        display: 'flex',
                        fontFamily: '"Lato", sans-serif',
                        fontSize: 18,
                        fontWeight: 700,
                        gap: 8,
                        height: 48,
                        justifyContent: 'center',
                        padding: '0 24px',
                        transition: 'transform 0.3s',
                        whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
                        <i className={'fas fa-calendar'} style={{ fontSize: 18 }} />
                        {'Schedule Inspection'}
                    </button>
                </div>
                {zipError && (
                    <p style={{
                        color: FIRE_GLOW,
                        fontFamily: '"Open Sans", sans-serif',
                        fontSize: 14,
                        margin: '8px 0 0 0'
                    }}>{zipError}</p>
                )}
            </form>
        )
    }

    const getCtaStack = () => {
        return (
            <div style={{
                marginBottom: 32,
                maxWidth: 440,
                width: '100%'
            }}>
                <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: 12
                }}>
                    <button
                    type={'button'}
                    onClick={onCallNow}
                    style={{
                        alignItems: 'center',
                        background: `linear-gradient(90deg, ${FIRE}, ${FIRE_GLOW})`,
                        border: 'none',
                        borderRadius: 8,
                        boxShadow: '0 10px 25px -8px rgba(214,61,31,0.7)',
                        color: '#FFFFFF',
                        cursor: 'pointer',
                        display: 'flex',
                        flex: isMobile ? 'none' : 1,
                        fontFamily: '"Lato", sans-serif',
                        fontSize: 18,
                        fontWeight: 700,
                        gap: 10,
                        height: 56,
                        justifyContent: 'center',
                        padding: '0 24px',
                        transition: 'transform 0.3s',
                        whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
                        <i className={'fas fa-phone'} style={{ fontSize: 18 }} />
                        {'Call Now'}
                    </button>
                    <button
                    type={'button'}
                    onClick={onCallMe}
                    style={{
                        alignItems: 'center',
                        backgroundColor: 'transparent',
                        border: '2px solid rgba(255,255,255,0.85)',
                        borderRadius: 8,
                        color: '#FFFFFF',
                        cursor: 'pointer',
                        display: 'flex',
                        flex: isMobile ? 'none' : 1,
                        fontFamily: '"Lato", sans-serif',
                        fontSize: 18,
                        fontWeight: 700,
                        gap: 10,
                        height: 56,
                        justifyContent: 'center',
                        padding: '0 24px',
                        transition: 'background-color 0.3s, color 0.3s',
                        whiteSpace: 'nowrap'
                    }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                        <i className={'fas fa-headset'} style={{ fontSize: 18 }} />
                        {'Call Me'}
                    </button>
                </div>
                <button
                type={'button'}
                onClick={onScheduleToggle}
                style={{
                    alignItems: 'center',
                    background: 'none',
                    border: 'none',
                    color: 'rgba(255,255,255,0.85)',
                    cursor: 'pointer',
                    display: 'flex',
                    fontFamily: '"Lato", sans-serif',
                    fontSize: 15,
                    fontWeight: 600,
                    gap: 8,
                    marginTop: 16,
                    padding: 0,
                    textDecoration: 'underline',
                    textUnderlineOffset: 3
                }}>
                    <i className={`fas ${showSchedule ? 'fa-chevron-up' : 'fa-calendar'}`} style={{ fontSize: 14 }} />
                    {showSchedule ? 'Hide scheduling' : 'Prefer to schedule an inspection instead?'}
                </button>
                {showSchedule && getZipForm()}
            </div>
        )
    }

    const getHeroCopy = () => {
        return (
            <div style={{
                display: 'flex',
                flex: isMobile ? 'none' : 1,
                flexDirection: 'column',
                maxWidth: 620,
                width: '100%'
            }}>
                <h1 style={{
                    color: '#FFFFFF',
                    fontFamily: '"Poppins", sans-serif',
                    fontSize: isMobile ? 36 : 56,
                    fontWeight: 700,
                    lineHeight: 1.1,
                    margin: 0,
                    marginBottom: 24
                }}>
                    {'Get a Free Emergency Fire Blanket '}
                    <span style={{
                        background: `linear-gradient(90deg, ${FIRE}, ${FIRE_GLOW})`,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                    }}>{'With Your Home Safety Inspection'}</span>
                </h1>
                <p style={{
                    color: '#FFFFFF',
                    fontFamily: '"Lato", sans-serif',
                    fontSize: isMobile ? 18 : 20,
                    fontWeight: 500,
                    lineHeight: 1.6,
                    margin: 0,
                    marginBottom: 20,
                    maxWidth: 560
                }}>{'A house fire gives you less than 3 minutes to act. We\'ll make sure your family is prepared.'}</p>
                {getCtaStack()}
                <div style={{
                    columnGap: 24,
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    rowGap: 12
                }}>
                    {[
                        'Family-First Protection',
                        'Whole-Home Coverage',
                        '24/7 Peace of Mind'
                    ].map((text, index) => (
                        <div
                        key={index}
                        style={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 8
                        }}>
                            <i
                            className={'fas fa-circle-check'}
                            style={{
                                color: PRIMARY_GREEN,
                                fontSize: 20
                            }} />
                            <span style={{
                                color: '#FFFFFF',
                                fontFamily: '"Lato", sans-serif',
                                fontSize: 14,
                                fontWeight: 500
                            }}>{text}</span>
                        </div>
                    ))}
                </div>
            </div>
        )
    }

    const getOfferCard = () => {
        return (
            <div style={{
                alignItems: isMobile ? 'center' : 'flex-end',
                display: 'flex',
                flexDirection: 'column',
                width: '100%'
            }}>
                <div style={{
                    maxWidth: 480,
                    position: 'relative',
                    width: '100%'
                }}>
                    <div style={{
                        background: `linear-gradient(90deg, ${FIRE} 0%, rgba(214,61,31,0.6) 50%, ${FIRE_GLOW} 100%)`,
                        borderRadius: 18,
                        bottom: -4,
                        filter: 'blur(16px)',
                        left: -4,
                        opacity: 0.75,
                        position: 'absolute',
                        right: -4,
                        top: -4
                    }} />
                    <div style={{
                        backgroundColor: '#FFFFFF',
                        borderRadius: 16,
                        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
                        overflow: 'hidden',
                        position: 'relative'
                    }}>
                        <div style={{
                            alignItems: 'center',
                            background: `linear-gradient(90deg, ${PRIMARY_GREEN}, ${ACCENT_GREEN})`,
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 8,
                            justifyContent: 'center',
                            padding: '12px 20px'
                        }}>
                            <i className={'fas fa-gift'} style={{ color: '#FFFFFF', fontSize: 14 }} />
                            <span style={{
                                color: '#FFFFFF',
                                fontFamily: '"Poppins", sans-serif',
                                fontSize: 14,
                                fontWeight: 700,
                                letterSpacing: 1.5,
                                textTransform: 'uppercase'
                            }}>{'Limited Time Offer'}</span>
                        </div>
                        <div style={{ padding: '16px 32px 32px 32px' }}>
                            <img
                            alt={'Your Emergency Fire Blanket'}
                            src={require('assets/fire-blanket.jpg')}
                            style={{
                                borderRadius: 8,
                                height: 256,
                                objectFit: 'contain',
                                width: '100%'
                            }} />
                            <div style={{ margin: '12px 0', textAlign: 'center' }}>
                                <span style={{
                                    color: SECONDARY_DARK,
                                    fontFamily: '"Poppins", sans-serif',
                                    fontSize: 26,
                                    fontWeight: 800,
                                    letterSpacing: 0.5
                                }}>{`Your Emergency Fire Blanket`}</span>
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{
                                    background: `linear-gradient(90deg, rgba(214,61,31,0.2), rgba(214,61,31,0.1), rgba(214,61,31,0.2))`,
                                    border: `2px solid rgba(214,61,31,0.3)`,
                                    borderRadius: 12,
                                    marginBottom: 16,
                                    overflow: 'hidden',
                                    padding: '16px 16px',
                                    position: 'relative'
                                }}>
                                    <div style={{
                                        alignItems: 'center',
                                        display: 'flex',
                                        flexDirection: 'row',
                                        gap: 12,
                                        justifyContent: 'center'
                                    }}>
                                        <span style={{
                                            color: SECONDARY_DARK,
                                            fontFamily: '"Poppins", sans-serif',
                                            fontSize: 36,
                                            fontWeight: 800,
                                            textDecoration: 'line-through',
                                            textDecorationThickness: 3
                                        }}>{'$99.99'}</span>
                                        <span style={{
                                            background: `linear-gradient(90deg, ${PRIMARY_GREEN}, ${ACCENT_GREEN})`,
                                            backgroundClip: 'text',
                                            fontFamily: '"Poppins", sans-serif',
                                            fontSize: 48,
                                            fontWeight: 900,
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent'
                                        }}>{'FREE'}</span>
                                    </div>
                                </div>
                                <h3 style={{
                                    color: SECONDARY_DARK,
                                    fontFamily: '"Poppins", sans-serif',
                                    fontSize: 20,
                                    fontWeight: 700,
                                    margin: '0 0 12px 0'
                                }}>{'Your First Line of Defense'}</h3>
                                <div style={{
                                    alignItems: 'flex-start',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 6,
                                    marginBottom: 16,
                                    paddingLeft: 16,
                                    textAlign: 'left'
                                }}>
                                    {[
                                        { text: 'No mess or residue', bold: false },
                                        { text: 'Never expires', bold: false },
                                        { text: 'Be prepared, not panicked', bold: true }
                                    ].map((item, index) => (
                                        <div
                                        key={index}
                                        style={{
                                            alignItems: 'flex-start',
                                            display: 'flex',
                                            flexDirection: 'row',
                                            gap: 8
                                        }}>
                                            <i
                                            className={'fas fa-check'}
                                            style={{
                                                color: PRIMARY_GREEN,
                                                fontSize: 14,
                                                marginTop: 4
                                            }} />
                                            <span style={{
                                                color: SECONDARY_DARK,
                                                fontFamily: '"Lato", sans-serif',
                                                fontSize: 15,
                                                fontWeight: item.bold ? 600 : 400
                                            }}>{item.text}</span>
                                        </div>
                                    ))}
                                </div>
                                <button
                                onClick={onClaimClick}
                                style={{
                                    alignItems: 'center',
                                    background: `linear-gradient(90deg, ${FIRE}, ${FIRE_GLOW})`,
                                    border: 'none',
                                    borderRadius: 8,
                                    color: '#FFFFFF',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    fontFamily: '"Lato", sans-serif',
                                    fontSize: 18,
                                    fontWeight: 700,
                                    gap: 8,
                                    margin: '8px 0',
                                    padding: '16px 40px',
                                    transition: 'transform 0.3s'
                                }}
                                onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; }}
                                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}>
                                    <i className={'fas fa-gift'} style={{ fontSize: 20 }} />
                                    {'Claim My Free Kit'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <React.Fragment>
        <section style={{
            alignItems: 'center',
            display: 'flex',
            minHeight: '85vh',
            overflow: 'hidden',
            position: 'relative',
            width: '100%'
        }}>
            <div style={{
                bottom: 0,
                left: 0,
                position: 'absolute',
                right: 0,
                top: 0
            }}>
                <video
                autoPlay
                loop
                muted
                playsInline
                src={require('assets/house-fire-video.mp4')}
                style={{
                    height: '100%',
                    objectFit: 'cover',
                    width: '100%'
                }} />
                <div style={{
                    background: `linear-gradient(to right, rgba(36,47,63,0.8), rgba(36,47,63,0.5), rgba(36,47,63,0))`,
                    bottom: 0,
                    left: 0,
                    position: 'absolute',
                    right: 0,
                    top: 0
                }} />
            </div>
            <div style={{
                margin: '0 auto',
                maxWidth: 1280,
                padding: isMobile ? '120px 16px 64px 16px' : '120px 16px 64px 16px',
                position: 'relative',
                width: '100%',
                zIndex: 10
            }}>
                <div style={{
                    alignItems: 'center',
                    display: 'grid',
                    gap: 32,
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr'
                }}>
                    {getHeroCopy()}
                    {getOfferCard()}
                </div>
            </div>
        </section>
        {callModalVisible && (
            <CallModal
            phoneNumber={phoneNumber}
            onClose={() => setCallModalVisible(false)} />
        )}
        </React.Fragment>
    )
}

export default Hero;
